// Build a personalized invite blurb. Falls back gracefully when no name is set.
export function composeInvite(hostName, joinUrl) {
  const trimmed = (hostName || "").trim();
  const who = trimmed.length ? trimmed : "Someone";
  return `${who} started a VibeCheck — you in? 😈 ${joinUrl}`;
}

// Robust clipboard copy with fallback for iframes, insecure contexts,
// and browsers that silently reject navigator.clipboard.writeText.
export async function copyText(text) {
  // Try modern async clipboard first
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch (e) {
    // fall through to legacy path
  }

  // Legacy fallback: hidden textarea + execCommand('copy')
  try {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.setAttribute("readonly", "");
    ta.style.position = "fixed";
    ta.style.top = "0";
    ta.style.left = "0";
    ta.style.opacity = "0";
    ta.style.pointerEvents = "none";
    document.body.appendChild(ta);
    ta.focus();
    ta.select();
    ta.setSelectionRange(0, text.length);
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    return ok;
  } catch (e) {
    return false;
  }
}

// Share with graceful fallback to clipboard copy. Returns 'shared' | 'copied' | 'failed'.
export async function shareOrCopy({ title, text, url }) {
  const payload = { title, text, url };
  if (navigator.share) {
    try {
      await navigator.share(payload);
      return "shared";
    } catch (e) {
      // User canceled the share sheet
      if (e && (e.name === "AbortError" || /aborted|cancel/i.test(e.message || ""))) {
        return "cancelled";
      }
      // Permission / NotAllowed / iframe denial → fall back to copy
    }
  }
  // Prefer the personalized text (which already includes the URL via composeInvite)
  // so the recipient gets the full invite blurb. Falls back to URL/empty.
  const fallback = text && text.length ? text : (url || "");
  const ok = await copyText(fallback);
  return ok ? "copied" : "failed";
}

// Open an external URL reliably. Falls back to copying the URL when popups are blocked.
export async function openExternal(url) {
  try {
    const w = window.open(url, "_blank", "noopener,noreferrer");
    if (w && !w.closed) return "opened";
  } catch (e) {
    // ignore, fall through
  }
  // Try same-tab navigation as secondary path (works for sms: and wa.me)
  try {
    window.location.href = url;
    return "opened";
  } catch (e) {
    const ok = await copyText(url);
    return ok ? "copied" : "failed";
  }
}
